import type {
  PartyResponse,
  QueueEntryResponse,
} from "@bw/shared/types/generated";
import { Avatar } from "../../../common/components/Avatar";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  PARTY_MEMBER_ACTIVITY_LABELS,
  PARTY_MEMBER_ADMISSION_LABELS,
  safePartyMemberName,
} from "../lib/partyMemberPresentation";

export type QueueGroupState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "mismatch" }
  | { kind: "solo" }
  | { kind: "party"; party: PartyResponse };

function formatJoinedTime(value: string, timeZone?: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
  };
  if (timeZone) options.timeZone = timeZone;

  try {
    return new Intl.DateTimeFormat(undefined, options).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }
}

function JoinedAt({
  joinedAt,
  timeZone,
}: {
  joinedAt: string;
  timeZone?: string;
}) {
  const label = formatJoinedTime(joinedAt, timeZone);
  return (
    <Typography variant="body2" color="text.secondary">
      Joined the queue{" "}
      <time dateTime={joinedAt}>{label ?? "time unavailable"}</time>
    </Typography>
  );
}

function QueueGroupUnavailable({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Alert severity="error" role="alert" aria-label="Queue group unavailable">
      <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
        <span>{message}</span>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          size="small"
          onClick={onRetry}
        >
          Retry group status
        </Button>
      </Stack>
    </Alert>
  );
}

export function QueueGroupSummary({
  entry,
  state,
  timeZone,
  onRetry,
}: {
  entry: QueueEntryResponse;
  state: QueueGroupState;
  timeZone?: string;
  onRetry: () => void;
}) {
  if (state.kind === "loading") {
    return (
      <Stack
        direction="row"
        spacing={1}
        role="status"
        aria-label="Loading queue group"
        sx={{ alignItems: "center" }}
      >
        <CircularProgress size={20} />
        <Typography color="text.secondary">
          Loading your queue group…
        </Typography>
      </Stack>
    );
  }

  if (state.kind === "error") {
    return (
      <QueueGroupUnavailable
        message="We couldn't load your queue group."
        onRetry={onRetry}
      />
    );
  }

  if (state.kind === "mismatch") {
    return (
      <QueueGroupUnavailable
        message="We couldn't verify who is queued with you."
        onRetry={onRetry}
      />
    );
  }

  if (state.kind === "solo") {
    return (
      <Paper
        component="section"
        variant="outlined"
        sx={{ p: 2 }}
        role="region"
        aria-label="Your queue group"
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
        >
          <Stack>
            <Typography component="h2" variant="h5">
              Queued solo
            </Typography>
            <JoinedAt joinedAt={entry.joined_at} timeZone={timeZone} />
          </Stack>
          <Chip label="1 player" size="small" />
        </Stack>
      </Paper>
    );
  }

  const members = state.party.eligibility.members ?? [];
  const accepted = state.party.capacity.accepted;
  const playerLabel =
    accepted > 0
      ? `${accepted} ${accepted === 1 ? "player" : "players"}`
      : "Roster unavailable";

  return (
    <Paper
      component="section"
      variant="outlined"
      role="region"
      aria-label="Your party"
      sx={{ overflow: "hidden" }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: "space-between", alignItems: "flex-start", p: 2 }}
      >
        <Stack>
          <Typography component="h2" variant="h5">
            Your party
          </Typography>
          <JoinedAt joinedAt={entry.joined_at} timeZone={timeZone} />
        </Stack>
        <Stack sx={{ alignItems: "flex-end" }}>
          <Chip label={playerLabel} size="small" />
          <Typography variant="caption" color="text.secondary">
            Queued together
          </Typography>
        </Stack>
      </Stack>

      <List disablePadding aria-label="Queued party members" aria-live="polite">
        {members.map((member, index) => {
          const name = safePartyMemberName(member, index);
          return (
            <ListItem key={member.member_id} divider sx={{ gap: 1.25, px: 2 }}>
              <Avatar name={name} avatarUrl={member.avatar_url} size={28} />
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography component="strong" sx={{ fontWeight: 700 }}>
                  {name}
                </Typography>
                {member.is_current_user ? (
                  <Typography variant="caption" color="text.secondary">
                    You
                  </Typography>
                ) : null}
                {member.is_leader ? (
                  <Chip
                    label="Leader"
                    size="small"
                    variant="outlined"
                    sx={{ alignSelf: "flex-start" }}
                  />
                ) : null}
              </Stack>
              <Stack sx={{ alignItems: "flex-end" }}>
                <Typography variant="body2">
                  {PARTY_MEMBER_ACTIVITY_LABELS[member.activity_state]}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {PARTY_MEMBER_ADMISSION_LABELS[member.admission_state]}
                </Typography>
              </Stack>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
}
